import { UserAvatar } from "./UserAvatar";
import { VerticalDotsIcon } from "./Svg";
import { DropdownMenu, DropdownMenuItem } from "./DropdownMenu";
import { UserRoleIds, UserRoleIds as UserRoles, UserRoleLabels, UserRoleColors } from "../constants";
import { getUserFullName } from "../store/models/User";
import { Button } from "./Button";
import { SelectInput } from "./form/SelectInput";
import React, { useState, useRef } from "react";
import _ from "lodash";
import cx from "classnames";

export const MemberItemModes = {
  MANAGE: "manage",
  INVITE: "invite"
}

const RoleMenuItem = ({ role, user, onClick, disabled }) => {

  return (
    <DropdownMenuItem
      disabled={disabled}
      label={UserRoleLabels[role]}
      selected={user.role === role}
      onClick={() => onClick(role)}
    />
  )

}
export const MemberItem = ({
  user, currentUser, mode = MemberItemModes.MANAGE,
  onInvite, onChangeRole, onRemove, allowedRoles = null, canManage = null
}) => {
  const isAdmin = currentUser?.role === UserRoles.ADMIN
  const canManageRoles = canManage !== null ? canManage : isAdmin
  const actionsDisabled = !canManageRoles || currentUser?.id === user.id
  const dropdownRef = useRef(null);

  const [selectedRole, setSelectedRole] = useState(user?.role ?? UserRoleIds.VIEWER)
  
  // Filter role options if allowedRoles is provided
  const allRoleOptions = _.map(UserRoleLabels, (label, key) => ({ label: label, value: key }))
  const roleOptions = allowedRoles 
    ? allRoleOptions.filter(option => allowedRoles.includes(option.value))
    : allRoleOptions

  const changeRole = async (role) => {
    onChangeRole(user, role)
  }

  const remove = () => {
    console.log('MemberItem: remove clicked for user:', user.email, user.id);
    console.log('MemberItem: calling onRemove callback');
    onRemove(user)
  }

  const invite = () => {
    onInvite(user, selectedRole)
  }

  const onSelectRole = (role) => {
    setSelectedRole(role.value)
  }

  const handleItemClick = (e) => {
    if (mode === MemberItemModes.MANAGE && canManageRoles) {
      // Don't trigger if clicking on the dropdown menu itself or its children
      if (dropdownRef.current?.contains(e.target)) {
        console.log('MemberItem: Click is on dropdown, skipping handleItemClick');
        return;
      }
      
      // Prevent event from bubbling up to parent elements (e.g., navigation links)
      e.stopPropagation();
      
      console.log('MemberItem: Simulating click on dropdown button');
      // Simulate click on the dropdown button
      const dropdownButton = dropdownRef.current?.querySelector('button');
      if (dropdownButton) {
        dropdownButton.click();
      }
    }
  }

  return (
    <div className={cx("MemberItem", { "MemberItem--clickable": mode === MemberItemModes.MANAGE && canManageRoles })} 
      onClick={handleItemClick}>
      <div className={"MemberItem__Left"}>
        <div className={"MemberItem__Avatar"} style={{ marginLeft: '2rem' }}>
          <UserAvatar user={user} />
        </div>
        <div className={"MemberItem__Info"}>
          {getUserFullName(user) && <div className={"MemberItem__Name"}>{getUserFullName(user)}</div>}
          <div className={"MemberItem__Email"}>{user.email}</div>
          <div className={"MemberItem__RoleBadge"} style={{ backgroundColor: UserRoleColors[user.role] }}>
            {UserRoleLabels[user.role]}
          </div>
        </div>
        {mode === MemberItemModes.MANAGE && canManageRoles && <div className={"MemberItem__Right"} ref={dropdownRef}>
          <DropdownMenu icon={<VerticalDotsIcon />}>
            {onChangeRole && (
              <DropdownMenu label={"Role"}>
                {(!allowedRoles || allowedRoles.includes(UserRoles.ADMIN)) && (
                  <RoleMenuItem
                    role={UserRoles.ADMIN}
                    user={user}
                    onClick={changeRole}
                    disabled={actionsDisabled}
                  />
                )}
                {(!allowedRoles || allowedRoles.includes(UserRoles.EDITOR)) && (
                  <RoleMenuItem
                    role={UserRoles.EDITOR}
                    user={user}
                    onClick={changeRole}
                    disabled={actionsDisabled}
                  />
                )}
                {(!allowedRoles || allowedRoles.includes(UserRoles.VIEWER)) && (
                  <RoleMenuItem
                    role={UserRoles.VIEWER}
                    user={user}
                    onClick={changeRole}
                    disabled={actionsDisabled}
                  />
                )}
                {(!allowedRoles || allowedRoles.includes(UserRoles.PARTNER)) && (
                  <RoleMenuItem
                    role={UserRoles.PARTNER}
                    user={user}
                    onClick={changeRole}
                    disabled={actionsDisabled}
                  />
                )}
                {(!allowedRoles || allowedRoles.includes(UserRoles.REPRESENTATIVE)) && (
                  <RoleMenuItem
                    role={UserRoles.REPRESENTATIVE}
                    user={user}
                    onClick={changeRole}
                    disabled={actionsDisabled}
                  />
                )}
              </DropdownMenu>
            )}

            <DropdownMenuItem label={user.disabled ? "Enable" : "Disable"}
              disabled={actionsDisabled}
              onClick={remove} />

          </DropdownMenu>
        </div>}
      </div>
      {mode === MemberItemModes.INVITE && <div className={"MemberItem__Right"}>
        <div className={"NewForm__InputWrapper NewForm__InputWrapper--floating"}>
          <SelectInput
            className={"InviteTeamMembersToProjectModal__InviteRow__RoleSelect"}
            options={roleOptions}
            isDisabled={user?.invited}
            value={{ label: UserRoleLabels[selectedRole], value: selectedRole }}
            onChange={onSelectRole} />
        </div>
        <Button color={"green-outline"}
          title={user?.invited ? "Invited" : "Invite"}
          disabled={user?.invited}
          onClick={() => user?.invited ? null : invite()}>
          Invite
        </Button>
      </div>}
    </div>
  )
}

export const MemberItemsList = ({
  users, currentUser, mode,
  onInvite, onChangeRole, onRemove, allowedRoles = null, canManage = null
}) => {
  return (
    <div className={"MemberItemsList"}>
      {users.map(user => <MemberItem key={user.id ?? user.email}
        user={user}
        currentUser={currentUser}
        mode={mode}
        onRemove={onRemove}
        onChangeRole={onChangeRole}
        onInvite={onInvite}
        allowedRoles={allowedRoles}
        canManage={canManage} />)}
    </div>
  )
}